import axios from "axios";
import { getToken } from "../../Shared/baseURL";
import { getFrontendErrorMessage } from "Shared/apiInterceptors";
import {
  NETWORK_FOLLOW_STATES,
  clearAutoNetworkFollowState,
  clearNetworkFollowState,
  getNetworkFollowState,
  inferNetworkFollowState,
  setNetworkFollowState,
  shouldCreateAutoNetworkFollow,
  shouldRemoveAutoNetworkFollow,
} from "Utils/networkFollowState";

const normalizeNumericId = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed.length) return null;
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : null;
  }
  return null;
};

export const isAlreadyFollowingMessage = (message) =>
  String(message || "").toLowerCase().includes("already follow");

const getRequestedFollowState = (options = {}) =>
  Object.prototype.hasOwnProperty.call(options, "followState")
    ? options.followState
    : NETWORK_FOLLOW_STATES.MANUAL_FOLLOW;

const rememberFollowState = (followerId, followeeId, state) => {
  if (!Object.values(NETWORK_FOLLOW_STATES).includes(state)) return;
  setNetworkFollowState(followerId, followeeId, state);
};

export const sendFollowRequest = async (myID, otherUserID, options = {}) => {
  let followeeId = null;
  const requestedFollowState = getRequestedFollowState(options);

  try {
    const token = await getToken();
    followeeId = normalizeNumericId(otherUserID) ?? otherUserID;
    if (followeeId == null || String(followeeId).trim() === "") {
      throw new Error("followeeId is required");
    }
    const res = await axios.post(
      `/sns/v1/followers`,
      { followeeId },
      {
        headers: {
          Authorization: token,
        },
      }
    );
    if (res.status >= 200 && res.status < 300) {
      const { data, status } = res;
      rememberFollowState(myID, followeeId, requestedFollowState);
      return {
        status,
        data,
      };
    }
  } catch (error) {
    if (error.response) {
      const { status } = error.response;
      const message = getFrontendErrorMessage(error);
      if (isAlreadyFollowingMessage(message)) {
        rememberFollowState(myID, followeeId, requestedFollowState);
      }
      return {
        status,
        message,
      };
    }
    return {
      status: 500,
      message: getFrontendErrorMessage(error) || "Unable to follow user.",
    };
  }
};

export const removeFollowRequest = async (otherUserID, options = {}) => {
  let followeeId = null;
  const {
    clearState = false,
    followState = null,
    followerId = null,
    ignoreMissing = false,
  } = options;

  try {
    const token = await getToken();
    followeeId = normalizeNumericId(otherUserID) ?? otherUserID;
    if (followeeId == null || String(followeeId).trim() === "") {
      throw new Error("followeeId is required");
    }

    const res = await axios.delete(`/sns/v1/followers/${followeeId}`, {
      headers: {
        Authorization: token,
      },
    });

    if (res.status >= 200 && res.status < 300) {
      if (clearState) {
        clearNetworkFollowState(followerId, followeeId);
      }
      rememberFollowState(followerId, followeeId, followState);
      return {
        status: res.status,
        data: res.data,
      };
    }
  } catch (error) {
    if (error.response) {
      const { data, status } = error.response;
      const message = getFrontendErrorMessage(error);
      const normalizedMessage = String(message || "").toLowerCase();

      if (
        ignoreMissing &&
        (status === 400 ||
          status === 404 ||
          normalizedMessage.includes("not follow") ||
          normalizedMessage.includes("not found"))
      ) {
        if (clearState) {
          clearNetworkFollowState(followerId, followeeId);
        }
        rememberFollowState(followerId, followeeId, followState);
        return {
          status,
          data,
          ignored: true,
        };
      }

      return {
        status,
        message,
      };
    }

    return {
      status: 500,
      message: getFrontendErrorMessage(error) || "Unable to unfollow user.",
    };
  }
};

export const syncConnectionRequestFollow = async (
  myID,
  otherUserID,
  followRecord = null,
  { forceAutoFollow = false } = {},
) => {
  const followeeId = normalizeNumericId(otherUserID) ?? otherUserID;
  if (followeeId == null || String(followeeId).trim() === "") {
    return {
      skipped: true,
      message: "followeeId is required",
      isFollowing: null,
    };
  }
  const storedFollowState = getNetworkFollowState(myID, followeeId);
  const serverFollowState = inferNetworkFollowState(followRecord);
  const hasManualUnfollow =
    storedFollowState === NETWORK_FOLLOW_STATES.MANUAL_UNFOLLOW ||
    serverFollowState === NETWORK_FOLLOW_STATES.MANUAL_UNFOLLOW;

  if (!forceAutoFollow && hasManualUnfollow) {
    const removeResult = await removeFollowRequest(followeeId, {
      followerId: myID,
      followState: NETWORK_FOLLOW_STATES.MANUAL_UNFOLLOW,
      ignoreMissing: true,
    });

    return {
      ...removeResult,
      followState: NETWORK_FOLLOW_STATES.MANUAL_UNFOLLOW,
      isFollowing: false,
    };
  }

  const hasManualFollow =
    storedFollowState === NETWORK_FOLLOW_STATES.MANUAL_FOLLOW ||
    serverFollowState === NETWORK_FOLLOW_STATES.MANUAL_FOLLOW ||
    !shouldCreateAutoNetworkFollow(myID, followeeId);
  const followState =
    forceAutoFollow || !hasManualFollow
      ? NETWORK_FOLLOW_STATES.AUTO
      : NETWORK_FOLLOW_STATES.MANUAL_FOLLOW;

  const followResult = await sendFollowRequest(myID, followeeId, {
    followState,
  });

  return {
    ...followResult,
    followState,
    isFollowing:
      !followResult?.message || isAlreadyFollowingMessage(followResult.message),
  };
};

export const removeAutoFollowForConnectionEnd = async (
  myID,
  otherUserID,
  followRecord = null,
) => {
  const followeeId = normalizeNumericId(otherUserID) ?? otherUserID;
  if (followeeId == null || String(followeeId).trim() === "") {
    return {
      skipped: true,
      followState: null,
      isFollowing: null,
    };
  }
  const storedFollowState = getNetworkFollowState(myID, followeeId);

  if (storedFollowState === NETWORK_FOLLOW_STATES.MANUAL_UNFOLLOW) {
    return {
      skipped: true,
      followState: NETWORK_FOLLOW_STATES.MANUAL_UNFOLLOW,
      isFollowing: false,
    };
  }

  if (storedFollowState === NETWORK_FOLLOW_STATES.MANUAL_FOLLOW) {
    return {
      skipped: true,
      followState: NETWORK_FOLLOW_STATES.MANUAL_FOLLOW,
      isFollowing: true,
    };
  }

  if (!shouldRemoveAutoNetworkFollow(myID, followeeId, followRecord)) {
    return {
      skipped: true,
      followState: storedFollowState,
      isFollowing: null,
    };
  }

  const removeResult = await removeFollowRequest(followeeId, {
    followerId: myID,
    ignoreMissing: true,
  });

  if (!removeResult?.message) {
    clearAutoNetworkFollowState(myID, followeeId);
  }

  return {
    ...removeResult,
    removed: !removeResult?.message,
    followState: NETWORK_FOLLOW_STATES.AUTO,
    isFollowing: removeResult?.message ? null : false,
  };
};

export const sendUnfollowRequest = (otherUserID, options = {}) =>
  removeFollowRequest(otherUserID, options);

export const followAfterConnectionRequest = async (
  myID,
  otherUserID,
  followRecord = null,
) => {
  const result = await syncConnectionRequestFollow(
    myID,
    otherUserID,
    followRecord,
  );

  if (result?.message && !isAlreadyFollowingMessage(result.message)) {
    throw new Error(result.message);
  }

  return result;
};

export const unfollowAutoFollowAfterConnectionRequest = (
  myID,
  otherUserID,
  followRecord = null,
) => removeAutoFollowForConnectionEnd(myID, otherUserID, followRecord);

export const clearConnectionRequestAutoFollow = (myID, otherUserID) => {
  clearAutoNetworkFollowState(myID, otherUserID);
};

export const sendConnectRequest = async (otherUserID, message = "") => {
  try {
    const token = await getToken();
    const res = await axios.post(
      `/sns/v1/connections/requests`,
      { toUserId: otherUserID, message },
      {
        headers: {
          Authorization: token,
        },
      }
    );
    if (res.status >= 200 || res.status < 300) {
      const { data, status } = res;
      return {
        status,
        data,
      };
    }
  } catch (error) {
    if (error.response) {
      const { status } = error.response;
      return {
        status,
        message: getFrontendErrorMessage(error),
      };
    }
  }
};

export const withdrawConnectRequest = async (requestId) => {
  try {
    const token = await getToken();
    const headers = {
      Authorization: token,
    };

    try {
      const res = await axios.delete(`/sns/v1/connections/requests/${requestId}`, {
        headers,
      });
      const { data, status } = res;
      return {
        status,
        data,
      };
    } catch (deleteError) {
      const status = deleteError?.response?.status;
      const message = String(deleteError?.response?.data?.message || deleteError?.message || "");

      if (status === 400 || status === 404 || message.toLowerCase().includes("invalid entity")) {
        const res = await axios.patch(
          `/sns/v1/connections/requests/${requestId}`,
          { status: "DECLINED" },
          { headers },
        );
        const { data, status: patchStatus } = res;
        return {
          status: patchStatus,
          data,
        };
      }

      throw deleteError;
    }
  } catch (error) {
    if (error.response) {
      const { status } = error.response;
      return {
        status,
        message: getFrontendErrorMessage(error),
      };
    }
  }
};
